import JWPlayer from '@jwplayer/jwplayer-react';
import {
    MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import { addAnnouncement } from '../stories/Store/toastSlice';
import logger from '../utility/logger';
import { useAppDispatch } from '.';
import type { LiveCaptionsFailure, LiveCaptionsMetadataEvent } from './useSlides';

const CAPTIONS_BUTTON_ID = 'toggleCCButton';
const CAPTIONS_BUTTON_CLASS = 'pli-icon-cc';
const CAPTIONS_PREFERENCE_KEY = 'watch.liveCaptionsEnabled';
const CAPTION_CUE_DURATION_SECONDS = 6;
const CAPTION_CUE_START_OFFSET_SECONDS = 0.25;
const LIVE_CAPTIONS_ANNOUNCEMENT_ID = 'live-captions-enabled';
const LIVE_CAPTIONS_CONTAINER_CLASS = 'live-captions';
const MAX_CAPTION_LINES = 2;
const MAX_CAPTION_CHARACTERS_PER_LINE = 32;
const LIVE_CAPTIONS_TELEMETRY_ACTION = 'Live captions lifecycle';

declare global {
    interface Window {
        WebVTT?: {
            processCues: (
                window: Window,
                cues: VTTCue[],
                overlay: HTMLElement,
                controls: boolean
            ) => void;
        };
    }
}

export const LIVE_CAPTIONS_SESSION_TIMEOUT_MS = 30_000;
export const LIVE_CAPTIONS_DELIVERY_TIMEOUT_MS = 5 * 60_000;
export const LIVE_CAPTIONS_DESYNC_THRESHOLD_MS = 5_000;

export type LiveCaptionsDriftSeverity = 'degraded' | 'severe' | 'critical';

export const getLiveCaptionsDriftSeverity = (
    absoluteDriftMs: number,
    thresholdMs: number,
): LiveCaptionsDriftSeverity => {
    if (absoluteDriftMs > thresholdMs * 2) return 'critical';
    if (absoluteDriftMs > thresholdMs * 1.5) return 'severe';
    return 'degraded';
};

export const LIVE_CAPTIONS_ENABLED_MESSAGE =
    'Live captioning has been turned on. Live captions are auto generated by voice detection and AI technology. Live captions may misrepresent what a presenter is saying.';

export type LiveCaptionsAnalyticsEvent = {
    feature: 'live_captions';
    request_type: 'service' | 'learner_enablement';
    lifecycle_event: string;
    terminal_outcome?: 'succeeded' | 'failed' | 'timed_out' | 'cancelled';
    feature_request_id: string;
    service_request_id: string;
    session_id?: string;
    provider: 'amazon_ivs';
    environment?: string;
    item_sk?: number;
    segment_item_sk?: number;
    duration_ms?: number;
    enabled_duration_ms?: number;
    failed_stage?: string;
    failure_category?: string;
    activation_source?: 'learner' | 'saved_preference' | 'recovery';
    end_reason?: string;
};

interface UseLiveCaptionsProps {
    playerRef: MutableRefObject<typeof JWPlayer>;
    enabled: boolean;
    captionsButtonIcon: string;
    sessionTimeoutMs?: number;
    deliveryTimeoutMs?: number;
    desyncThresholdMs?: number;
    cleTrackingActive?: boolean;
    programItemSk?: number;
    segmentItemSk?: number;
    sessionGuid?: string;
    environment?: string | null;
    onAnalyticsEvent?: (event: LiveCaptionsAnalyticsEvent) => void;
}

type CaptionExpectation = {
    requestId: string;
    requestedAtMs: number;
    trackedDurationMs: number;
    trackingStartedAtMs?: number;
    started: boolean;
    delivered: boolean;
    timedOut: boolean;
    failureObserved: boolean;
    ended: boolean;
};

const readCaptionsPreference = () => {
    try {
        return window.localStorage.getItem(CAPTIONS_PREFERENCE_KEY) === 'true';
    } catch {
        return false;
    }
};

const saveCaptionsPreference = (enabled: boolean) => {
    try {
        window.localStorage.setItem(CAPTIONS_PREFERENCE_KEY, String(enabled));
    } catch {
        return;
    }
};

const normalizeCaptionText = (text: string) => text.replace(/\s+/g, ' ').trim();

const chunkLongWord = (word: string) => {
    const chunks = [];

    for (let index = 0; index < word.length; index += MAX_CAPTION_CHARACTERS_PER_LINE) {
        chunks.push(word.slice(index, index + MAX_CAPTION_CHARACTERS_PER_LINE));
    }

    return chunks;
};

export const wrapLiveCaptionText = (text: string) => {
    const normalizedText = normalizeCaptionText(text);
    if (!normalizedText) return [];

    const words = normalizedText
        .split(' ')
        .flatMap((word) => chunkLongWord(word));
    const lines = words.reduce<string[]>((wrappedLines, word) => {
        const currentLine = wrappedLines[wrappedLines.length - 1];

        if (!currentLine || currentLine.length >= MAX_CAPTION_CHARACTERS_PER_LINE) {
            wrappedLines.push(word);
        } else {
            wrappedLines[wrappedLines.length - 1] = `${currentLine} ${word}`;
        }

        return wrappedLines;
    }, []);

    return lines.slice(-MAX_CAPTION_LINES);
};

export const useLiveCaptions = ({
    playerRef,
    enabled,
    captionsButtonIcon,
    sessionTimeoutMs = LIVE_CAPTIONS_SESSION_TIMEOUT_MS,
    deliveryTimeoutMs = LIVE_CAPTIONS_DELIVERY_TIMEOUT_MS,
    desyncThresholdMs = LIVE_CAPTIONS_DESYNC_THRESHOLD_MS,
    cleTrackingActive = false,
    programItemSk,
    segmentItemSk,
    sessionGuid,
    environment,
    onAnalyticsEvent,
}: UseLiveCaptionsProps) => {
    const dispatch = useAppDispatch();
    const activeCueRef = useRef<VTTCue>();
    const captionSessionTimerRef = useRef<number>();
    const captionTextTimerRef = useRef<number>();
    const deliveryTimerRef = useRef<number>();
    const captionsAvailableRef = useRef(false);
    const cleTrackingActiveRef = useRef(cleTrackingActive);
    const disconnectedAtMsRef = useRef<number>();
    const expectationRef = useRef<CaptionExpectation>();
    const learnerEnabledAtMsRef = useRef<number>();
    const learnerRequestIdRef = useRef<string>();
    const loggedFailuresRef = useRef(new Set<string>());
    const liveCaptionsContainerRef = useRef<HTMLElement>();
    const [captionsAvailable, setCaptionsAvailable] = useState(false);
    const [captionsEnabled, setCaptionsEnabled] = useState(readCaptionsPreference);
    const captionsEnabledRef = useRef(captionsEnabled);
    const allowedRef = useRef(enabled);
    allowedRef.current = enabled;
    cleTrackingActiveRef.current = cleTrackingActive;

    const getTrackedDurationMs = useCallback((expectation: CaptionExpectation) =>
        expectation.trackedDurationMs + (
            expectation.trackingStartedAtMs === undefined
                ? 0
                : Date.now() - expectation.trackingStartedAtMs
        ), []);

    const emitLifecycle = useCallback((
        outcome: string,
        properties: Record<string, unknown> = {},
        failure = false,
    ) => {
        const expectation = expectationRef.current;
        if (!expectation) return;
        const terminalOutcome: LiveCaptionsAnalyticsEvent['terminal_outcome'] = outcome === 'ended'
            ? expectation.timedOut
                ? 'timed_out'
                : expectation.failureObserved
                    ? 'failed'
                    : expectation.delivered
                        ? 'succeeded'
                        : 'cancelled'
            : undefined;

        const telemetry = {
            feature: 'live_captions',
            provider: 'amazon_ivs',
            environment: environment || undefined,
            request_id: expectation.requestId,
            session_id: sessionGuid || undefined,
            item_sk: programItemSk,
            segment_item_sk: segmentItemSk,
            request_time: new Date(expectation.requestedAtMs).toISOString(),
            event_time: new Date().toISOString(),
            outcome,
            request_type: 'service',
            lifecycle_event: outcome,
            terminal_outcome: terminalOutcome,
            duration_ms: Date.now() - expectation.requestedAtMs,
            tracked_duration_ms: getTrackedDurationMs(expectation),
            ...properties,
        };

        if (failure) {
            logger.error(LIVE_CAPTIONS_TELEMETRY_ACTION, telemetry);
        } else {
            logger.log(LIVE_CAPTIONS_TELEMETRY_ACTION, telemetry);
        }

        onAnalyticsEvent?.({
            feature: 'live_captions',
            request_type: 'service',
            lifecycle_event: outcome,
            terminal_outcome: terminalOutcome,
            feature_request_id: expectation.requestId,
            service_request_id: expectation.requestId,
            session_id: sessionGuid || undefined,
            provider: 'amazon_ivs',
            environment: environment || undefined,
            item_sk: programItemSk,
            segment_item_sk: segmentItemSk,
            duration_ms: telemetry.duration_ms,
            failed_stage: typeof properties.failed_stage === 'string' ? properties.failed_stage : undefined,
            failure_category: typeof properties.failure_category === 'string' ? properties.failure_category : undefined,
        });
    }, [environment, getTrackedDurationMs, onAnalyticsEvent, programItemSk, segmentItemSk, sessionGuid]);

    const ensureExpectation = useCallback(() => {
        if (expectationRef.current) return expectationRef.current;

        const expectation: CaptionExpectation = {
            requestId: uuidv4(),
            requestedAtMs: Date.now(),
            trackedDurationMs: 0,
            started: false,
            delivered: false,
            timedOut: false,
            failureObserved: false,
            ended: false,
        };
        expectationRef.current = expectation;
        loggedFailuresRef.current.clear();

        emitLifecycle('requested');

        return expectation;
    }, [emitLifecycle]);

    const emitLearnerLifecycle = useCallback((
        lifecycleEvent: string,
        properties: Partial<LiveCaptionsAnalyticsEvent> = {},
    ) => {
        const expectation = expectationRef.current;
        if (!expectation) return;

        const learnerRequestId = learnerRequestIdRef.current ?? uuidv4();
        learnerRequestIdRef.current = learnerRequestId;
        onAnalyticsEvent?.({
            feature: 'live_captions',
            request_type: 'learner_enablement',
            lifecycle_event: lifecycleEvent,
            feature_request_id: learnerRequestId,
            service_request_id: expectation.requestId,
            session_id: sessionGuid || undefined,
            provider: 'amazon_ivs',
            environment: environment || undefined,
            item_sk: programItemSk,
            segment_item_sk: segmentItemSk,
            ...properties,
        });
    }, [environment, onAnalyticsEvent, programItemSk, segmentItemSk, sessionGuid]);

    const startLearnerEnablement = useCallback((
        activationSource: NonNullable<LiveCaptionsAnalyticsEvent['activation_source']>,
    ) => {
        if (learnerEnabledAtMsRef.current !== undefined) return;

        ensureExpectation();
        learnerRequestIdRef.current = uuidv4();
        learnerEnabledAtMsRef.current = Date.now();
        emitLearnerLifecycle('enabled', {
            activation_source: activationSource,
            enabled_duration_ms: 0,
        });
    }, [emitLearnerLifecycle, ensureExpectation]);

    const endLearnerEnablement = useCallback((lifecycleEvent: 'disabled' | 'ended', endReason: string) => {
        if (learnerEnabledAtMsRef.current === undefined) return;

        emitLearnerLifecycle(lifecycleEvent, {
            enabled_duration_ms: Date.now() - learnerEnabledAtMsRef.current,
            end_reason: endReason,
        });
        learnerEnabledAtMsRef.current = undefined;
        learnerRequestIdRef.current = undefined;
    }, [emitLearnerLifecycle]);

    const endServiceExpectation = useCallback((endReason: string) => {
        const expectation = expectationRef.current;
        if (!expectation || expectation.ended) return;

        expectation.ended = true;
        emitLifecycle('ended', { end_reason: endReason });
    }, [emitLifecycle]);

    const logFailureOnce = useCallback((
        failure: LiveCaptionsFailure,
        properties: Record<string, unknown> = {},
        dedupeSuffix?: string,
    ) => {
        const expectation = ensureExpectation();
        const failureKey = [failure.stage, failure.category, dedupeSuffix]
            .filter(Boolean)
            .join(':');
        if (loggedFailuresRef.current.has(failureKey)) return;

        loggedFailuresRef.current.add(failureKey);
        expectation.failureObserved = true;
        emitLifecycle('failed', {
            ...properties,
            failed_stage: failure.stage,
            failure_category: failure.category,
        }, true);
    }, [emitLifecycle, ensureExpectation]);

    const getVideoElement = useCallback(() => {
        const playerContainer = playerRef.current?.getContainer?.() as HTMLElement | undefined;
        return playerContainer?.querySelector('video') ?? null;
    }, [playerRef]);

    const renderActiveCue = useCallback(() => {
        const playerContainer = playerRef.current?.getContainer?.() as HTMLElement | undefined;
        const activeCue = activeCueRef.current;
        const webVttRenderer = window.WebVTT;
        const shouldRender =
            allowedRef.current
            && captionsAvailableRef.current
            && captionsEnabledRef.current
            && Boolean(activeCue);

        if (!shouldRender) {
            liveCaptionsContainerRef.current?.replaceChildren();
            liveCaptionsContainerRef.current?.classList.remove('jw-captions-enabled');
            return;
        }

        if (!activeCue) return;
        if (!playerContainer) {
            logFailureOnce({ stage: 'render', category: 'player_container_unavailable' });
            return;
        }
        if (!webVttRenderer) {
            logFailureOnce({ stage: 'render', category: 'webvtt_renderer_unavailable' });
            return;
        }

        try {
            const jwCaptionsContainer = Array
                .from(playerContainer.querySelectorAll<HTMLElement>('.jw-captions'))
                .find((container) => !container.classList.contains(LIVE_CAPTIONS_CONTAINER_CLASS));
            let captionsContainer = liveCaptionsContainerRef.current;

            if (!captionsContainer?.isConnected) {
                captionsContainer = document.createElement('div');
                captionsContainer.className = `jw-captions jw-reset ${LIVE_CAPTIONS_CONTAINER_CLASS}`;
                playerContainer.appendChild(captionsContainer);
                liveCaptionsContainerRef.current = captionsContainer;
            }

            captionsContainer.style.fontSize = jwCaptionsContainer?.style.fontSize ?? '';
            captionsContainer.replaceChildren();
            captionsContainer.classList.toggle('jw-captions-enabled', shouldRender);
            webVttRenderer.processCues(
                window,
                [activeCue],
                captionsContainer,
                true
            );
        } catch {
            liveCaptionsContainerRef.current?.replaceChildren();
            liveCaptionsContainerRef.current?.classList.remove('jw-captions-enabled');
            logFailureOnce({ stage: 'render', category: 'webvtt_render_failed' });
        }
    }, [logFailureOnce, playerRef]);

    const removeLiveCaptionsContainer = useCallback(() => {
        liveCaptionsContainerRef.current?.remove();
        liveCaptionsContainerRef.current = undefined;
    }, []);

    const removeCaptionsButton = useCallback(() => {
        try {
            playerRef.current?.removeButton?.(CAPTIONS_BUTTON_ID);
        } catch {
            return;
        }
    }, [playerRef]);

    const clearActiveCue = useCallback(() => {
        window.clearTimeout(captionTextTimerRef.current);
        captionTextTimerRef.current = undefined;
        activeCueRef.current = undefined;
        renderActiveCue();
    }, [renderActiveCue]);

    const markCaptionsUnavailable = useCallback((unexpected = false) => {
        window.clearTimeout(captionSessionTimerRef.current);
        captionSessionTimerRef.current = undefined;
        if (unexpected && captionsAvailableRef.current && cleTrackingActiveRef.current) {
            const expectation = ensureExpectation();
            expectation.failureObserved = true;
            disconnectedAtMsRef.current = Date.now();
            emitLifecycle('failed', {
                failed_stage: 'ivs_delivery',
                failure_category: 'caption_session_disconnected',
                timeout_ms: sessionTimeoutMs,
            }, true);
        }
        if (captionsAvailableRef.current) {
            endLearnerEnablement('ended', unexpected ? 'service_unavailable' : 'caption_session_ended');
        }
        captionsAvailableRef.current = false;
        setCaptionsAvailable(false);
        clearActiveCue();
        removeCaptionsButton();
    }, [clearActiveCue, emitLifecycle, endLearnerEnablement, ensureExpectation, removeCaptionsButton, sessionTimeoutMs]);

    const markCaptionsAvailable = useCallback(() => {
        if (!allowedRef.current) return;

        const isRecovery = disconnectedAtMsRef.current !== undefined;
        if (disconnectedAtMsRef.current !== undefined) {
            emitLifecycle('recovered', {
                stage: 'ivs_delivery',
                unavailable_duration_ms: Date.now() - disconnectedAtMsRef.current,
            });
            disconnectedAtMsRef.current = undefined;
        }

        captionsAvailableRef.current = true;
        setCaptionsAvailable(true);
        if (captionsEnabledRef.current) {
            startLearnerEnablement(isRecovery ? 'recovery' : 'saved_preference');
        }

        window.clearTimeout(captionSessionTimerRef.current);
        captionSessionTimerRef.current = window.setTimeout(
            () => markCaptionsUnavailable(true),
            sessionTimeoutMs
        );
    }, [emitLifecycle, markCaptionsUnavailable, sessionTimeoutMs, startLearnerEnablement]);

    const replaceActiveCue = useCallback((text: string) => {
        const video = getVideoElement();
        if (!video) {
            logFailureOnce({ stage: 'render', category: 'video_element_unavailable' });
            return;
        }
        if (typeof window.VTTCue !== 'function') {
            logFailureOnce({ stage: 'render', category: 'vtt_cue_unavailable' });
            return;
        }

        clearActiveCue();

        const currentTime = video.currentTime;
        const cue = new VTTCue(
            Math.max(0, currentTime - CAPTION_CUE_START_OFFSET_SECONDS),
            currentTime + CAPTION_CUE_DURATION_SECONDS,
            text
        );
        activeCueRef.current = cue;
        renderActiveCue();
        captionTextTimerRef.current = window.setTimeout(
            clearActiveCue,
            CAPTION_CUE_DURATION_SECONDS * 1000
        );
    }, [clearActiveCue, getVideoElement, logFailureOnce, renderActiveCue]);

    const handleLiveCaptionsMetadata = useCallback((
        metadata: LiveCaptionsMetadataEvent,
        liveLatencySeconds?: number,
    ) => {
        if (!enabled) return;

        const expectation = ensureExpectation();

        if (!expectation.started) {
            expectation.started = true;
            emitLifecycle('started', { stage: 'ivs_delivery' });
        }

        if (metadata.type === 'live-caption-heartbeat') {
            markCaptionsAvailable();
            return;
        }

        const lines = wrapLiveCaptionText(metadata.text);
        if (!lines.length) return;

        if (
            typeof metadata.timestampUtcMs === 'number'
            && Number.isFinite(metadata.timestampUtcMs)
        ) {
            const rawDriftMs = Date.now() - metadata.timestampUtcMs;
            const liveLatencyMs = typeof liveLatencySeconds === 'number'
                && Number.isFinite(liveLatencySeconds)
                && liveLatencySeconds >= 0
                ? liveLatencySeconds * 1000
                : undefined;
            const driftMs = rawDriftMs - (liveLatencyMs ?? 0);
            if (Math.abs(driftMs) > desyncThresholdMs) {
                const failureCategory = driftMs > 0 ? 'caption_late' : 'caption_early';
                const driftSeverity = getLiveCaptionsDriftSeverity(
                    Math.abs(driftMs),
                    desyncThresholdMs,
                );
                logFailureOnce({
                    stage: 'synchronization',
                    category: failureCategory,
                }, {
                    drift_ms: driftMs,
                    raw_drift_ms: rawDriftMs,
                    live_latency_ms: liveLatencyMs,
                    threshold_ms: desyncThresholdMs,
                    drift_severity: driftSeverity,
                    caption_is_final: metadata.isFinal,
                }, driftSeverity);
            }
        }

        if (!expectation.delivered) {
            expectation.delivered = true;
            window.clearTimeout(deliveryTimerRef.current);
            deliveryTimerRef.current = undefined;
            expectation.trackedDurationMs = getTrackedDurationMs(expectation);
            expectation.trackingStartedAtMs = undefined;
            emitLifecycle('delivered', { stage: 'ivs_delivery' });
        }

        markCaptionsAvailable();
        replaceActiveCue(lines.join('\n'));
    }, [desyncThresholdMs, emitLifecycle, enabled, ensureExpectation, getTrackedDurationMs, logFailureOnce, markCaptionsAvailable, replaceActiveCue]);

    const handleLiveCaptionsFailure = useCallback((failure: LiveCaptionsFailure) => {
        if (!enabled) return;
        logFailureOnce(failure);
    }, [enabled, logFailureOnce]);

    const toggleCaptions = useCallback(() => {
        if (!allowedRef.current) return;

        const enabled = !captionsEnabledRef.current;
        captionsEnabledRef.current = enabled;
        setCaptionsEnabled(enabled);
        saveCaptionsPreference(enabled);
        renderActiveCue();

        if (enabled) {
            startLearnerEnablement('learner');
            dispatch(addAnnouncement({
                contentType: 'text',
                id: LIVE_CAPTIONS_ANNOUNCEMENT_ID,
                message: LIVE_CAPTIONS_ENABLED_MESSAGE,
                needsReceiptUpdate: false,
            }));
        } else {
            endLearnerEnablement('disabled', 'learner');
        }
    }, [dispatch, endLearnerEnablement, renderActiveCue, startLearnerEnablement]);

    useEffect(() => {
        if (!enabled || !captionsAvailable) return;
        if (!playerRef.current) {
            logFailureOnce({ stage: 'initialization', category: 'player_api_unavailable' });
            return;
        }

        try {
            removeCaptionsButton();
            playerRef.current.addButton(
                captionsButtonIcon,
                `Turn ${captionsEnabled ? 'off' : 'on'} live captions`,
                toggleCaptions,
                CAPTIONS_BUTTON_ID,
                captionsEnabled
                    ? `${CAPTIONS_BUTTON_CLASS} pli-icon-cc--on`
                    : CAPTIONS_BUTTON_CLASS
            );
        } catch {
            logFailureOnce({ stage: 'initialization', category: 'caption_control_init_failed' });
            return;
        }

        const captionsButton = playerRef.current
            .getContainer?.()
            ?.querySelector(`.${CAPTIONS_BUTTON_CLASS}`);
        captionsButton?.setAttribute('aria-pressed', String(captionsEnabled));

        return removeCaptionsButton;
    }, [
        captionsAvailable,
        captionsButtonIcon,
        captionsEnabled,
        enabled,
        logFailureOnce,
        playerRef,
        removeCaptionsButton,
        toggleCaptions,
    ]);

    useEffect(() => {
        if (!enabled) {
            window.clearTimeout(deliveryTimerRef.current);
            deliveryTimerRef.current = undefined;
            endLearnerEnablement('ended', 'captions_ineligible');
            endServiceExpectation('captions_ineligible');
            expectationRef.current = undefined;
            loggedFailuresRef.current.clear();
            return;
        }

        ensureExpectation();
    }, [enabled, endLearnerEnablement, endServiceExpectation, ensureExpectation]);

    useEffect(() => {
        const handlePageExit = (event: PageTransitionEvent) => {
            if (event.persisted) return;
            endLearnerEnablement('ended', 'page_exit');
            endServiceExpectation('page_exit');
        };

        window.addEventListener('pagehide', handlePageExit);
        return () => window.removeEventListener('pagehide', handlePageExit);
    }, [endLearnerEnablement, endServiceExpectation]);

    useEffect(() => {
        const expectation = expectationRef.current;
        if (!enabled || !cleTrackingActive || !expectation || expectation.delivered || expectation.timedOut) return;

        expectation.trackingStartedAtMs = Date.now();
        const remainingMs = Math.max(0, deliveryTimeoutMs - expectation.trackedDurationMs);
        deliveryTimerRef.current = window.setTimeout(() => {
            expectation.trackedDurationMs = getTrackedDurationMs(expectation);
            expectation.trackingStartedAtMs = undefined;
            expectation.timedOut = true;
            deliveryTimerRef.current = undefined;
            emitLifecycle('timed_out', {
                failed_stage: 'ivs_delivery',
                failure_category: 'caption_not_delivered',
                timeout_ms: deliveryTimeoutMs,
            }, true);
        }, remainingMs);

        return () => {
            window.clearTimeout(deliveryTimerRef.current);
            deliveryTimerRef.current = undefined;
            if (expectation.trackingStartedAtMs !== undefined) {
                expectation.trackedDurationMs = getTrackedDurationMs(expectation);
                expectation.trackingStartedAtMs = undefined;
            }
        };
    }, [cleTrackingActive, deliveryTimeoutMs, emitLifecycle, enabled, getTrackedDurationMs]);

    useEffect(() => {
        if (enabled) return;

        markCaptionsUnavailable();
        removeLiveCaptionsContainer();
    }, [enabled, markCaptionsUnavailable, removeLiveCaptionsContainer]);

    const cleanupRef = useRef<() => void>();
    cleanupRef.current = () => {
        endLearnerEnablement('ended', 'component_unmounted');
        endServiceExpectation('component_unmounted');
        window.clearTimeout(captionSessionTimerRef.current);
        window.clearTimeout(captionTextTimerRef.current);
        window.clearTimeout(deliveryTimerRef.current);
        clearActiveCue();
        captionsAvailableRef.current = false;
        removeCaptionsButton();
        removeLiveCaptionsContainer();
    };

    useEffect(() => () => cleanupRef.current?.(), []);

    return {
        captionsAvailable,
        captionsEnabled,
        handleLiveCaptionsMetadata,
        handleLiveCaptionsFailure,
    };
};
